import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router-dom";
import MatchLocationMap from "./MatchLocationMap";
import {
  cancelUSPSAMatchRegistration,
  createUSPSAMatchRegistration,
  getMyUSPSAMatchRegistration,
  getUSPSAMatch,
  getUSPSAMatchRegistrationSummary,
  updateUSPSAMatchRegistration,
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { getCompetitionAuthProfile } from "../../lib/competitionAuthProfile";
import { CompetitionErrorState } from "../Competition/CompetitionRoutePanels";
import { formatCalendarDate } from "../../utils/date";
import "../Leaderboard/Leaderboard.css";

const POWER_FACTOR_OPTIONS = [
  { value: "", label: "Select" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
];

function formatDate(value) {
  if (!value) return "-";
  return formatCalendarDate(value);
}

function formatStatus(value) {
  return String(value || "unknown")
    .trim()
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function normalizeRuleFamily(value) {
  return String(value || "competition").trim().toLowerCase();
}

function normalizeMatchLevel(value) {
  return String(value || "Level I").trim();
}

function buildLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) return primary;
  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();
  if (city && state) return `${city}, ${state}`;
  if (range && state) return `${range}, ${state}`;
  if (range) return range;
  if (state) return state;
  return "Location not set";
}

function buildDraft(item = {}) {
  return {
    displayName: String(item.displayName || ""),
    email: String(item.email || ""),
    competitorNumber: String(item.competitorNumber || ""),
    uspsaMemberNumber: String(item.uspsaMemberNumber || ""),
    divisionId: String(item.divisionId || ""),
    classification: String(item.classification || ""),
    category: String(item.category || ""),
    powerFactor: String(item.powerFactor || ""),
    notes: String(item.notes || ""),
  };
}

function normalizeDraft(draft = {}) {
  return {
    displayName: String(draft.displayName || "").trim(),
    email: String(draft.email || "").trim(),
    competitorNumber: String(draft.competitorNumber || "").trim(),
    uspsaMemberNumber: String(draft.uspsaMemberNumber || "").trim(),
    divisionId: String(draft.divisionId || "").trim(),
    classification: String(draft.classification || "").trim(),
    category: String(draft.category || "").trim(),
    powerFactor: String(draft.powerFactor || "").trim().toLowerCase(),
    notes: String(draft.notes || "").trim(),
  };
}

function registrationChanged(registration, draft) {
  const normalized = normalizeDraft(draft);
  return (
    normalized.displayName !== String(registration?.displayName || "").trim() ||
    normalized.email !== String(registration?.email || "").trim() ||
    normalized.competitorNumber !== String(registration?.competitorNumber || "").trim() ||
    normalized.uspsaMemberNumber !== String(registration?.uspsaMemberNumber || "").trim() ||
    normalized.divisionId !== String(registration?.divisionId || "").trim() ||
    normalized.classification !== String(registration?.classification || "").trim() ||
    normalized.category !== String(registration?.category || "").trim() ||
    normalized.powerFactor !== String(registration?.powerFactor || "").trim().toLowerCase() ||
    normalized.notes !== String(registration?.notes || "").trim()
  );
}

function USPSAMatchRegistrationDetail() {
  const location = useLocation();
  const { matchId } = useParams();
  const signInPath = buildAuthReturnPath("/auth/sign-in", buildCurrentReturnTo(location, `/matches/uspsa/${encodeURIComponent(matchId)}/register`));
  const [match, setMatch] = useState(null);
  const [summary, setSummary] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [draft, setDraft] = useState(buildDraft());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [signInRequired, setSignInRequired] = useState(false);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setActionError("");
      setMessage("");
      setSignInRequired(false);

      try {
        const [matchValue, summaryValue] = await Promise.all([
          getUSPSAMatch(matchId),
          getUSPSAMatchRegistrationSummary(matchId),
        ]);

        let profileDraft = {};
        let existingRegistration = null;
        try {
          const profile = await getCompetitionAuthProfile();
          if (profile?.userId) {
            profileDraft = {
              displayName: String(profile.displayName || "").trim(),
              email: String(profile.email || "").trim(),
              uspsaMemberNumber: String(profile.uspsaId || "").trim(),
            };
            existingRegistration = await getMyUSPSAMatchRegistration(matchId);
          } else {
            setSignInRequired(true);
          }
        } catch {
          existingRegistration = null;
          setSignInRequired(true);
        }

        if (!active) return;
        setMatch(matchValue);
        setSummary(summaryValue);
        setRegistration(existingRegistration);
        setDraft(buildDraft(existingRegistration || profileDraft));
        setShowOptionalDetails(Boolean(existingRegistration));
      } catch (loadError) {
        if (!active) return;
        setError(loadError?.message || "Failed to load USPSA competition registration");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [matchId]);

  const registrationOpen = summary?.registrationOpen === true;
  const alreadyRegistered = !!registration;
  const registrationStatus = String(registration?.status || "").trim().toLowerCase();
  const canEditRegistration = registrationStatus === "pending" || registrationStatus === "confirmed";
  const dirty = alreadyRegistered ? registrationChanged(registration, draft) : true;
  const activeRegistrationCount = Number(summary?.pending || 0) + Number(summary?.confirmed || 0);
  const isSteelChallenge = normalizeRuleFamily(match?.ruleFamily) === "steel_challenge";
  const requiresMembershipId = normalizeMatchLevel(match?.level) !== "Level I";
  const normalizedDraft = normalizeDraft(draft);
  const canSubmitRegistration = Boolean(
    normalizedDraft.displayName && (!requiresMembershipId || normalizedDraft.uspsaMemberNumber)
  );
  const divisionLabel = isSteelChallenge ? "Steel Challenge Division" : "Division";
  const registrationTitle = alreadyRegistered ? "Manage registration" : "Register";
  const registrationReadinessItems = useMemo(() => {
    const hasDisplayName = Boolean(normalizedDraft.displayName);
    const hasMemberNumber = !requiresMembershipId || Boolean(normalizedDraft.uspsaMemberNumber);
    const hasDivision = Boolean(normalizedDraft.divisionId);

    return [
      {
        label: "Account",
        value: signInRequired ? "Sign in" : "Signed in",
        state: signInRequired ? "missing" : "ready"
      },
      {
        label: "Required",
        value: hasDisplayName && hasMemberNumber ? "Ready" : "Missing",
        state: hasDisplayName && hasMemberNumber ? "ready" : "missing"
      },
      {
        label: divisionLabel,
        value: hasDivision ? normalizedDraft.divisionId : "Optional",
        state: hasDivision ? "ready" : "neutral"
      },
      {
        label: "Status",
        value: alreadyRegistered ? formatStatus(registration.status) : registrationOpen ? "Open" : "Closed",
        state: alreadyRegistered || registrationOpen ? "ready" : "missing"
      }
    ];
  }, [
    alreadyRegistered,
    divisionLabel,
    normalizedDraft.displayName,
    normalizedDraft.divisionId,
    normalizedDraft.uspsaMemberNumber,
    registration?.status,
    registrationOpen,
    requiresMembershipId,
    signInRequired
  ]);

  function setDraftField(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCreateRegistration() {
    setBusy(true);
    setActionError("");
    setMessage("");
    try {
      const created = await createUSPSAMatchRegistration(matchId, normalizeDraft(draft));
      const nextSummary = await getUSPSAMatchRegistrationSummary(matchId);
      setRegistration(created);
      setSummary(nextSummary);
      setDraft(buildDraft(created));
      setMessage("Registration submitted.");
    } catch (createError) {
      setActionError(createError?.message || "Failed to register for this USPSA match");
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveRegistration() {
    if (!registration?.registrationId) return;
    setBusy(true);
    setActionError("");
    setMessage("");
    try {
      const updated = await updateUSPSAMatchRegistration(matchId, registration.registrationId, normalizeDraft(draft));
      setRegistration(updated);
      setDraft(buildDraft(updated));
      setMessage("Registration updated.");
    } catch (saveError) {
      setActionError(saveError?.message || "Failed to update registration");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelRegistration() {
    if (!registration?.registrationId) return;
    setBusy(true);
    setActionError("");
    setMessage("");
    try {
      const cancelled = await cancelUSPSAMatchRegistration(matchId, registration.registrationId);
      setRegistration(cancelled);
      setDraft(buildDraft(cancelled));
      setMessage("Registration cancelled.");
    } catch (cancelError) {
      setActionError(cancelError?.message || "Failed to cancel registration");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="leaderboard-page">
      <Helmet>
        <title>{match?.matchName ? `${match.matchName} Registration | Range Pocket Competition` : "USPSA Competition Registration | Range Pocket Competition"}</title>
      </Helmet>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Registration</div>
          <strong>Loading USPSA registration options</strong>
          <span>Checking account, division, and match status</span>
        </div>
      )}
      {!loading && error && (
        <CompetitionErrorState
          eyebrow="USPSA registration"
          title="Registration could not load"
          message={error}
          onRetry={() => window.location.reload()}
          links={[
            { to: `/matches/uspsa/${encodeURIComponent(matchId)}`, label: "Back to Match" },
            { to: "/matches", label: "Find Matches" },
          ]}
          className="registration-error-panel"
        />
      )}

      {!loading && !error && match && (
        <div className="leaderboard-stack">
          <div className="leaderboard-detail-hero registration-detail-hero">
            <div className="leaderboard-detail-copy">
              <Link to={`/matches/uspsa/${encodeURIComponent(matchId)}`} className="leaderboard-detail-back-link">
                <span aria-hidden="true">←</span>
                <span>Match</span>
              </Link>
              <h1>{match.matchName || "USPSA Competition Registration"}</h1>
              <div className="leaderboard-detail-chip-row">
                <span className={`status-pill status-${String(match?.status || "unknown").replace(/\s+/g, "_")}`}>
                  {formatStatus(match.status)}
                </span>
                <span className="open-match-chip">{isSteelChallenge ? "Steel Challenge" : "USPSA"}</span>
                <span className="open-match-chip">{registrationOpen ? "Registration open" : "Registration closed"}</span>
              </div>
              <div className="leaderboard-detail-facts registration-detail-facts">
                <div>
                  <span>Date</span>
                  <strong>{formatDate(match.matchDate || match.startDate)}</strong>
                </div>
                <div>
                  <span>Location</span>
                  <strong>{buildLocationLabel(match)}</strong>
                </div>
                <div>
                  <span>Registered</span>
                  <strong>{activeRegistrationCount}</strong>
                </div>
                <div>
                  <span>Your status</span>
                  <strong>{alreadyRegistered ? formatStatus(registration.status) : signInRequired ? "Sign in" : "Not registered"}</strong>
                </div>
              </div>
              <div className="leaderboard-detail-actions registration-detail-hero-actions">
                {signInRequired ? (
                  <Link to={signInPath} className="match-search-button leaderboard-inline-link">
                    Sign in to register
                  </Link>
                ) : (
                  <a href="#uspsa-registration" className="match-search-button leaderboard-inline-link">
                    {alreadyRegistered ? "Manage registration" : "Register"}
                  </a>
                )}
              </div>
            </div>

            <div className="leaderboard-detail-map-card registration-detail-map-card">
              <div>
                <span className="leaderboard-detail-kicker">Venue</span>
                <strong>{buildLocationLabel(match)}</strong>
              </div>
              <MatchLocationMap
                matches={[match]}
                selectedMatchId={match.matchId}
                emptyMessage="This match does not have latitude and longitude yet."
              />
            </div>
          </div>

          <div id="uspsa-registration" className="leaderboard-card owner-registration-panel registration-panel-card">
            <div className="owner-registration-form-header">
              <div>
                <div className="owner-registration-section-label">Registration</div>
                <h2>{registrationTitle}</h2>
              </div>
              <span className="open-match-chip">{isSteelChallenge ? "Steel Challenge" : "USPSA"}</span>
            </div>
            <div className="registration-readiness-strip" aria-label="Registration readiness">
              {registrationReadinessItems.map((item) => (
                <div key={item.label} className={`registration-readiness-item is-${item.state}`}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="owner-registration-form-body">
              {signInRequired ? (
                <div className="registration-inline-signin">
                  <span>Use your Range Pocket account to register.</span>
                  <Link to={signInPath} className="match-search-button leaderboard-inline-link">
                    Sign In
                  </Link>
                </div>
              ) : (
                <>
                  <div className="owner-registration-form-section">
                    <div className="owner-registration-create-grid">
                      <label className="leaderboard-field owner-registration-name-field">
                        <span>
                          Display Name
                          <em className="leaderboard-required-mark">Required</em>
                        </span>
                        <input className="match-search" value={draft.displayName} onChange={(event) => setDraftField("displayName", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>Email</span>
                        <input className="match-search" value={draft.email} onChange={(event) => setDraftField("email", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>{divisionLabel}</span>
                        <input className="match-search" value={draft.divisionId} onChange={(event) => setDraftField("divisionId", event.target.value)} />
                      </label>
                      <label className="leaderboard-field">
                        <span>Classification</span>
                        <input className="match-search" value={draft.classification} onChange={(event) => setDraftField("classification", event.target.value)} />
                      </label>
                      {!isSteelChallenge && (
                        <label className="leaderboard-field">
                          <span>Power Factor</span>
                          <select className="match-sort" value={draft.powerFactor} onChange={(event) => setDraftField("powerFactor", event.target.value)}>
                            {POWER_FACTOR_OPTIONS.map((option) => (
                              <option key={option.value || "empty"} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      {requiresMembershipId && (
                        <label className="leaderboard-field">
                          <span>
                            USPSA Member #
                            <em className="leaderboard-required-mark">Required</em>
                          </span>
                          <input className="match-search" value={draft.uspsaMemberNumber} onChange={(event) => setDraftField("uspsaMemberNumber", event.target.value)} />
                        </label>
                      )}
                    </div>

                    <details
                      className="owner-registration-optional-details"
                      open={showOptionalDetails}
                      onToggle={(event) => setShowOptionalDetails(event.currentTarget.open)}
                    >
                      <summary>Optional details</summary>
                      <div className="owner-registration-create-grid owner-registration-optional-grid">
                        <label className="leaderboard-field">
                          <span>Competitor #</span>
                          <input className="match-search" value={draft.competitorNumber} onChange={(event) => setDraftField("competitorNumber", event.target.value)} />
                        </label>
                        {!requiresMembershipId && (
                          <label className="leaderboard-field">
                            <span>USPSA Member #</span>
                            <input className="match-search" value={draft.uspsaMemberNumber} onChange={(event) => setDraftField("uspsaMemberNumber", event.target.value)} />
                          </label>
                        )}
                        <label className="leaderboard-field">
                          <span>Category</span>
                          <input className="match-search" value={draft.category} onChange={(event) => setDraftField("category", event.target.value)} />
                        </label>
                        <label className="leaderboard-field owner-registration-affiliations-field">
                          <span>Notes</span>
                          <input className="match-search" value={draft.notes} onChange={(event) => setDraftField("notes", event.target.value)} />
                        </label>
                      </div>
                    </details>
                  </div>

                  <div className="registration-action-strip">
                    {!alreadyRegistered ? (
                      <button
                        type="button"
                        className="match-search-button owner-registration-submit"
                        onClick={handleCreateRegistration}
                        disabled={busy || signInRequired || !registrationOpen || !canSubmitRegistration}
                      >
                        {busy ? "Working…" : "Register"}
                      </button>
                    ) : (
                      <div className="owner-banner-actions">
                        <button
                          type="button"
                          className="match-search-button owner-registration-submit"
                          onClick={handleSaveRegistration}
                          disabled={busy || !canEditRegistration || !dirty}
                        >
                          {busy ? "Working…" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="match-search-button"
                          onClick={handleCancelRegistration}
                          disabled={busy || !registrationOpen}
                        >
                          {busy ? "Working…" : "Cancel Registration"}
                        </button>
                        <Link to="/matches/my" className="registration-auth-secondary leaderboard-inline-link">
                          My Matches
                        </Link>
                      </div>
                    )}

                    {!registrationOpen && (
                      <div className="leaderboard-meta registration-action-note">
                        Registration is closed for this match.
                      </div>
                    )}
                    {message && <div className="leaderboard-success-inline">{message}</div>}
                    {actionError && <div className="leaderboard-error-inline">{actionError}</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export const USPSAMatchRegistrationDetail_Page = {
  path: "/matches/uspsa/:matchId/register",
  element: <USPSAMatchRegistrationDetail />,
};
